import { getDeviceAndUserStore, getDelegationStore } from '../storage/stores';
import {
  fetchDeviceProfileData,
  createDevice,
  replaceDevice,
  getAccountDeviceUID,
  getDeviceProfileId,
  DeviceType,
} from '../api/device';
import { fetchProfileName, getProfileID, createProfile } from '../api/profile';
import { OnBoardingError } from '../lib/errors/OnBoardingError';
import { onConsumedQuotaUpdated } from '../screenTime';
import { identifySentry } from '../lib/vendor/sentry';
import { uuidv4 } from '../lib/wrappers';
import { captureException, Severity } from '../helper/sentry';

export const initDeviceAndUser = async (deviceUid: string) => {
  // Get and persist device info to the storage
  const deviceStore = getDeviceAndUserStore();
  const { profileId } = await fetchDeviceProfileData(deviceUid);
  const profileName = await fetchProfileName(profileId);
  await deviceStore.set({
    device: deviceUid,
    user: '1',
    consumedQuotaSeconds: 0,
    profileId,
    profileName,
  });

  // Identify Sentry with the deviceUID as soon as it's persisted to the storage
  identifySentry();
};

export const updateConsumedQuota = async (consumedQuotaSeconds: number) => {
  const deviceUserStore = getDeviceAndUserStore();
  const deviceUserStoreData = deviceUserStore.get();
  if (!deviceUserStoreData) {
    throw new Error('Device User Store not properly initialized');
  }

  const { device, user, profileId, profileName } = deviceUserStoreData;

  await deviceUserStore.set({
    device,
    user,
    consumedQuotaSeconds,
    profileId,
    profileName,
  });

  console.info(`Consumed quota updated to ${consumedQuotaSeconds} seconds.`);

  return onConsumedQuotaUpdated();
};

/**
 * Check if the profile exist, if not creates it,
 * check if the device exist, if not creates it,
 * and returns the deviceUID
 */
export const getOrCreateDeviceAndProfile = async (
  deviceName: string,
  profileName: string,
  deviceType: DeviceType
): Promise<string> => {
  const profileId = await getOrCreateUserProfileId(profileName);

  return getOrCreateUserDeviceUId(deviceName, profileId, deviceType);
};

export const getOrCreateUserProfileId = (
  profileName: string
): Promise<number> =>
  createOrApplyFn(
    () => createProfile(profileName).then(profile => profile.id),
    () => getProfileID(profileName) as Promise<number>
  );

export const getOrCreateUserDeviceUId = async (
  deviceName: string,
  profileId: number,
  deviceType: DeviceType
): Promise<string> =>
  createOrApplyFn(
    () =>
      createDevice(deviceName, profileId, deviceType).then(
        device => device.uid
      ),
    () => replaceDevice(deviceName, profileId, deviceType)
  );

/**
 * Acquires the delegated device for this installation in an idempotent way.
 *
 * The ordering is what prevents duplicates:
 * 1. Reuse the persisted device if it still exists in the backend.
 * 2. Otherwise reuse the persisted device name (or generate one) and persist it
 *    BEFORE the POST, so a retry after a crash or a lost response collides (409)
 *    and adopts the same device instead of creating a new one.
 * 3. Persist the resulting uid immediately so no later step can recreate it.
 */
export const acquireDelegatedDevice = async (
  profileId: number
): Promise<string> => {
  const store = getDeviceAndUserStore();
  const delegationStore = getDelegationStore();

  const persisted = store.get();
  // Reuse the persisted device only when it still exists AND is assigned to the
  // current profile in the backend. Reusing a device assigned to another profile
  // would tie this student's rules and events to the wrong device.
  let belongsToAnotherProfile = false;
  if (persisted?.device) {
    const backendProfileId = await getDeviceProfileId(persisted.device);
    if (backendProfileId === profileId) {
      return persisted.device;
    }
    belongsToAnotherProfile = backendProfileId !== undefined;
  }

  const persistedDeviceName = delegationStore.getDeviceName();
  if (persisted?.device || persistedDeviceName) {
    // Re-acquiring because a previous onboarding did not complete. Tracked to
    // confirm the duplicate rate drops to ~0 once this fix is rolled out.
    captureException(
      new Error('Re-acquiring delegated device after incomplete onboarding'),
      {
        level: Severity.Warning,
        extra: {
          hadStalePersistedUid: !!persisted?.device,
          hadPersistedDeviceName: !!persistedDeviceName,
        },
      }
    );
  }

  // Reuse the persisted name for crash recovery (so a retry adopts on a 409),
  // but never the name of a device assigned to another profile: that POST would
  // 409 and then fail to adopt (wrong profile), leaving onboarding stuck.
  const deviceName = belongsToAnotherProfile
    ? uuidv4()
    : persistedDeviceName ?? uuidv4();
  // Persist the name (not the uid, which only exists after the POST) BEFORE the
  // request. It lives in the delegation store on purpose: writing it into the
  // device/user store would make `isFullyConfigured()` report a not-yet-onboarded
  // device.
  await delegationStore.setDeviceName(deviceName);

  const deviceUid = await getOrAdoptDelegatedDeviceUId(deviceName, profileId);
  // Single all-or-nothing write of the device/user store once the uid is known.
  // `profileName` starts empty to honour the store type and is filled right
  // after by the best-effort fetch (and self-healed on startup if it fails).
  await store.set({
    device: deviceUid,
    user: '1',
    profileId,
    profileName: '',
    // Preserve any quota already accounted for this installation; default to 0
    // only for a genuinely fresh enrollment, never grant free time on adoption.
    consumedQuotaSeconds: persisted?.consumedQuotaSeconds ?? 0,
  });

  return deviceUid;
};

/**
 * Persists the profile name outside the onboarding critical path.
 *
 * Best-effort: failures are reported but never block login, since the profile
 * name is not required for the device to be considered logged-in.
 */
export const persistDelegatedProfileName = async (profileId: number) => {
  try {
    const profileName = await fetchProfileName(profileId);
    await getDeviceAndUserStore().set({ profileName });
  } catch (error) {
    captureException(error as Error);
  }
};

/**
 * Self-heals a missing profile name when the service worker starts.
 *
 * Delegation onboarding persists the profile name outside the device-creation
 * critical path, so a crash in between can leave it empty. Re-fetch it when the
 * device is known but the name is missing. Never blocks login nor throws.
 */
export const ensureDelegatedProfileNamePersisted = async () => {
  const data = getDeviceAndUserStore().get();
  if (data?.device && data.profileId && !data.profileName) {
    await persistDelegatedProfileName(data.profileId);
  }
};

const getOrAdoptDelegatedDeviceUId = (
  deviceName: string,
  profileId: number
): Promise<string> =>
  createOrApplyFn(
    () =>
      createDevice(deviceName, profileId, 'delegated_chromebook').then(
        device => device.uid
      ),
    // On 409 the device already exists for this name: adopt it (reuse its uid)
    // instead of deleting and recreating, to avoid uid churn.
    () => adoptExistingDelegatedDevice(deviceName, profileId)
  );

const adoptExistingDelegatedDevice = async (
  deviceName: string,
  profileId: number
): Promise<string> => {
  try {
    return await getAccountDeviceUID(deviceName, profileId);
  } catch (error) {
    // A 409 told us the name is taken but we still cannot adopt it (e.g. it is
    // assigned to a different profile). Surface it so the otherwise-silent retry
    // loop -- which never logs in nor creates a device -- is detectable.
    captureException(error as Error, {
      level: Severity.Warning,
      extra: { reason: 'delegated-device-adoption-failed' },
    });
    throw error;
  }
};

/**
 * Creates a resource if it doesn't exist, otherwise applies a function.
 * @param createFn - Function that creates the resource. It should throw an
 * OnBoardingError if the resource already exists with a 409 status code.
 * @param fn - Fall back function that is applied if the resource already
 * exists. It should return the same type as the createFn.
 */
const createOrApplyFn = async <T>(
  createFn: () => Promise<T>,
  fn: () => Promise<T>
): Promise<T> => {
  const alreadyExistsErrorCode = 409;

  try {
    return await createFn();
  } catch (error) {
    if (
      error instanceof OnBoardingError &&
      error.statusCode === alreadyExistsErrorCode
    ) {
      return await fn();
    } else {
      throw error;
    }
  }
};
